import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Link
} from '@mui/material'
import {
  CheckCircle,
  Error as ErrorIcon,
  Info,
  OpenInNew
} from '@mui/icons-material'
import { IntegrationsService } from '../../../../shared/services/IntegrationsService'

export default function TeamsIntegration() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [webhookUrl, setWebhookUrl] = useState('')
  const [isConfigured, setIsConfigured] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    loadTeamsSettings()
  }, [])

  const loadTeamsSettings = async () => {
    try {
      setLoading(true)
      const settings = await IntegrationsService.getTeamsIntegration()
      setEnabled(settings.enabled)
      setWebhookUrl(settings.webhookUrl || '')
      setIsConfigured(settings.isConfigured)
    } catch (error: any) {
      console.error('Failed to load Teams settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setSaveError(null)
      setTestResult(null)

      await IntegrationsService.saveTeamsIntegration({
        enabled,
        webhookUrl: webhookUrl.trim() || undefined
      })

      setIsConfigured(!!webhookUrl.trim())

      // Show success message briefly
      setTimeout(() => {
        setTestResult({ success: true, message: 'Teams integration settings saved successfully' })
        setTimeout(() => setTestResult(null), 3000)
      }, 100)
    } catch (error: any) {
      setSaveError(error.response?.data?.message || 'Failed to save Teams integration settings')
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    if (!webhookUrl.trim()) {
      setTestResult({ success: false, message: 'Please enter a webhook URL first' })
      return
    }

    try {
      setTesting(true)
      setTestResult(null)

      const result = await IntegrationsService.testTeamsWebhook({
        webhookUrl: webhookUrl.trim()
      })

      setTestResult(result)
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.message || 'Failed to test webhook. Please check your URL.'
      })
    } finally {
      setTesting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Microsoft Teams integration?')) {
      return
    }

    try {
      setSaving(true)
      await IntegrationsService.disconnectTeamsIntegration()

      setEnabled(false)
      setWebhookUrl('')
      setIsConfigured(false)
      setTestResult(null)

      setTestResult({ success: true, message: 'Teams integration disconnected successfully' })
      setTimeout(() => setTestResult(null), 3000)
    } catch (error: any) {
      setSaveError('Failed to disconnect Teams integration')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        Microsoft Teams Integration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Send real-time ticket notifications to your Microsoft Teams channel using Power Automate
      </Typography>

      {/* Info Card */}
      <Card sx={{ mb: 3, bgcolor: 'info.lighter' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Info color="info" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                How it works
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create a Power Automate flow in your Microsoft Teams to receive webhook notifications.
                We'll send ticket updates to your channel automatically.
              </Typography>
              <Link
                href="/admin/docs/teams-integration"
                target="_blank"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                View Setup Guide
                <OpenInNew fontSize="small" />
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Enable Teams Notifications</Typography>
                <Typography variant="caption" color="text.secondary">
                  {enabled ? 'Active - Notifications will be sent to Teams' : 'Inactive - No notifications will be sent'}
                </Typography>
              </Box>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Power Automate Webhook URL"
            placeholder="https://prod-xx.westus.logic.azure.com:443/workflows/..."
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            helperText="Paste your Power Automate webhook URL here. See setup guide for instructions."
            disabled={!enabled}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || testing || !webhookUrl.trim()}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleTest}
              disabled={saving || testing || !webhookUrl.trim()}
            >
              {testing ? <CircularProgress size={20} /> : 'Test Connection'}
            </Button>

            {isConfigured && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisconnect}
                disabled={saving || testing}
              >
                Disconnect
              </Button>
            )}
          </Box>
        </Grid>

        {/* Test Result */}
        {testResult && (
          <Grid item xs={12}>
            <Alert
              severity={testResult.success ? 'success' : 'error'}
              icon={testResult.success ? <CheckCircle /> : <ErrorIcon />}
            >
              {testResult.message}
            </Alert>
          </Grid>
        )}

        {/* Save Error */}
        {saveError && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          </Grid>
        )}

        {/* Connection Status */}
        {isConfigured && (
          <Grid item xs={12}>
            <Card sx={{ bgcolor: enabled ? 'success.lighter' : 'grey.100' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {enabled ? (
                    <CheckCircle color="success" />
                  ) : (
                    <Info color="disabled" />
                  )}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {enabled ? 'Connected and Active' : 'Connected but Inactive'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {enabled
                        ? 'Notifications are being sent to your Teams channel'
                        : 'Enable notifications above to start sending updates'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Events Section */}
      {isConfigured && enabled && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Notification Events
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your Teams channel will receive notifications for the following events:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Ticket Created
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    When a new support ticket is submitted
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Ticket Assigned
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    When a ticket is assigned to an agent
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Ticket Resolved
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    When a ticket is marked as resolved
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Status Updated
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    When a ticket status changes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  )
}
